import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowUp, Sparkles } from 'lucide-react'
import { AnnouncementPill } from '@/components/landing/announcement-pill'
import { RotatingPlaceholder } from '@/components/landing/rotating-placeholder'
import { useChat } from '@/components/generals/chat-assistant'
import { heroExamples } from '@/constants/home'

const AskBar: FC = (): ReactNode => {
    const { ask } = useChat()
    const [value, setValue] = useState('')
    const [exampleIndex, setExampleIndex] = useState(0)
    const [focused, setFocused] = useState(false)

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        ask(value.trim() || heroExamples[exampleIndex])
        setValue('')
    }

    return (
        <form onSubmit={submit} className='fc-hero-in relative mx-auto w-full max-w-[600px]' style={{ animationDelay: '0.75s' }}>
            <span aria-hidden='true' className='fc-input-bloom' />

            <div className='relative flex h-[60px] items-center gap-3 rounded-full bg-white pl-2.5 pr-2 shadow-[0_1px_2px_rgba(14,14,14,0.05),0_18px_40px_-22px_rgba(54,30,140,0.45)] ring-1 ring-[#e4e2ee] transition-shadow focus-within:ring-2 focus-within:ring-violet sm:h-16'>
                <span className='hidden shrink-0 items-center gap-1.5 rounded-full bg-violet-soft px-3 py-1.5 text-[13px] font-semibold text-violet sm:inline-flex'>
                    <Sparkles className='size-3.5' aria-hidden='true' />
                    Ask FlowCraft
                </span>

                <label htmlFor='hero-ask' className='sr-only'>
                    Ask FlowCraft
                </label>

                <div className='relative h-full min-w-0 flex-1 pl-2 sm:pl-0'>
                    <input
                        id='hero-ask'
                        value={value}
                        onChange={event => setValue(event.target.value)}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        autoComplete='off'
                        className='absolute inset-0 h-full w-full bg-transparent text-[16px] text-ink focus:outline-none'
                    />

                    <RotatingPlaceholder
                        items={heroExamples}
                        hidden={value.length > 0}
                        onIndexChange={setExampleIndex}
                        className='inset-y-0 left-0 right-2 flex items-center whitespace-nowrap text-left text-[16px]'
                    />
                </div>

                <button
                    type='submit'
                    aria-label={focused || value ? 'Send question' : 'Ask FlowCraft this example'}
                    className='inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-violet text-white shadow-[0_6px_14px_-6px_rgba(93,45,230,0.8)] transition-colors hover:bg-violet-bright focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet sm:size-12'
                >
                    <ArrowUp className='size-[18px]' aria-hidden='true' />
                </button>
            </div>
        </form>
    )
}

export const HomeHero: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative -mt-16 overflow-hidden pt-16'>
            <div aria-hidden='true' className='fc-hero-glow' />

            <div className='fc-container relative flex flex-col items-center pb-16 pt-16 text-center sm:pt-20 md:pb-20 md:pt-28'>
                <div className='fc-hero-in max-w-full'>
                    <AnnouncementPill />
                </div>

                <h1 id='hero-title' className='fc-hero-in mt-7 text-[clamp(2.5rem,6.6vw,4.75rem)] font-medium leading-[1.02] tracking-[-0.045em] text-ink' style={{ animationDelay: '0.18s' }}>
                    <span className='block'>Connect every tool.</span>
                    <span className='block text-silver'>Automate every handoff.</span>
                </h1>

                <p className='fc-hero-in mt-6 max-w-[620px] text-[17px] leading-relaxed text-slate md:text-lg' style={{ animationDelay: '0.45s' }}>
                    FlowCraft links all your systems and builds the workflows for you, so your software works as one team without the engineering backlog.
                </p>

                <div className='mt-10 w-full md:mt-12'>
                    <AskBar />
                </div>
            </div>
        </section>
    )
}
